<script setup lang="ts">
import { api } from '@/api';
import { type QaEquipmentRtSystemDto } from '@/api/generator';
import PtwBackButton from '@/components/buttons/PtwBackButton.vue';
import { useVeeForm } from '@/components/forms/useVeeForm';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageStatus from '@/components/pages/BasePage/BasePageStatus.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { useQaEquipmentStore } from '@/stores/qaEquipmentStore.ts';
import { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import { reloadEquipmentKey } from '@/views/qaEquipment/types.ts';
import {
  getManufacturerLabel,
  getModelLabel,
  getTypeLabel,
  isRegistrablePtwQaEquipment,
} from '@/views/qaEquipment/utils';
import { useTranslation } from 'i18next-vue';
import { onBeforeUnmount, onMounted, provide } from 'vue';
import QaEquipmentForm from './components/QaEquipmentForm.vue';
import { entityNotFoundErrorHandler } from '@/errorHandling';
import { useRouter } from 'vue-router';

interface EntitiesDetailsViewProps {
  id: string;
}

const props = defineProps<EntitiesDetailsViewProps>();

const { t } = useTranslation();
const qaEquipmentStore = useQaEquipmentStore();

const router = useRouter();

const { action: loadEquipment, loading } = useLoading(async () => {
  const { result } = await api.qaEquipmentApi.getQaEquipmentRtSystem(
    {
      id: props.id,
    },
    [entityNotFoundErrorHandler(router)],
  );

  if (result) {
    resetForm({ values: result });
    qaEquipmentStore.setDeviceForRegistration(
      isRegistrablePtwQaEquipment(result) ? result : undefined,
    );
  }
}, true);

// Allow the Dialog to reload the equipment after the de-/registration
const reloadEquipment = () => {
  loadEquipment();
};
provide(reloadEquipmentKey, reloadEquipment);

const {
  form: { resetForm, values },
} = useVeeForm<QaEquipmentRtSystemDto>({
  mode: 'details',
});

onMounted(loadEquipment);
onBeforeUnmount(() => {
  qaEquipmentStore.setDeviceForRegistration(undefined);
});
</script>

<template>
  <BasePage :loading="loading">
    <template #title>
      <BasePageTitle
        :title="values.name"
        :titleId="values.iuid"
        :subtitle="`${t(getTypeLabel(values) as I18nKey)} | ${t(
          getManufacturerLabel(values) as I18nKey,
        )} | ${getModelLabel(values)}`"
      />
    </template>

    <template #status>
      <BasePageStatus :entity="values" />
    </template>

    <template #actions>
      <PtwBackButton
        :to="{ name: routenames.qaEquipment.overview }"
        :disabled="qaEquipmentStore.deRegistrationPending"
      />
    </template>

    <QaEquipmentForm />
  </BasePage>
</template>
